import React from "react";
import styles from "./grouped-items-list.module.css";
import { CollapsibleGroup } from "../collapsible-group/collapsible-group";
import cn from "classnames";

export interface Group<T> {
  id: string;
  name: React.ReactNode;
  color: string;
  items: T[];
  disableCollapse?: boolean;
}

export interface GroupedItemsListProps<T> {
  groups: Group<T>[];
  renderItem: (item: T, groupColor: string) => React.ReactNode;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  loadMoreText?: string;
  loadingText?: string;
  className?: string;
  groupClassName?: string;
  getEmptyMessage?: (group: Group<T>) => string;
  defaultCollapsedGroups?: string[];
  onGroupToggle?: (groupId: string, isCollapsed: boolean) => void;
}

export function GroupedItemsList<T>({
  groups,
  renderItem,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  loadMoreText = "Load more items",
  loadingText = "Loading...",
  className,
  groupClassName,
  getEmptyMessage,
  defaultCollapsedGroups = [],
  onGroupToggle,
}: GroupedItemsListProps<T>) {
  return (
    <div className={cn(styles.container, className)}>
      {groups.map((group) => {
        const isEmpty = group.items.length === 0;
        const emptyMessage = getEmptyMessage?.(group) || "No items available";

        return (
          <CollapsibleGroup
            key={group.id}
            id={group.id}
            title={group.name}
            color={group.color}
            defaultCollapsed={defaultCollapsedGroups.includes(group.id)}
            onToggle={onGroupToggle}
            isEmpty={isEmpty}
            emptyStateMessage={emptyMessage}
            className={groupClassName}
            showChevron={!group.disableCollapse}
          >
            <div className={styles.itemsContainer}>
              {group.items.map((item, index) => (
                <React.Fragment key={index}>{renderItem(item, group.color)}</React.Fragment>
              ))}
            </div>
          </CollapsibleGroup>
        );
      })}

      {hasMore && onLoadMore && (
        <button
          disabled={isLoading}
          className={cn(styles.loadMoreButton, { [styles.loading]: isLoading })}
          onClick={onLoadMore}
        >
          {isLoading ? loadingText : loadMoreText}
        </button>
      )}
    </div>
  );
}
